import useClickOutside from '@/hooks/useClickOutside'
import { RefObject, useRef, useState } from 'react'
import Text from '@/components/content/Text'
import { Icon, VStack } from '@musinsa/mwms-ds'
import styled, { css } from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import { BaseCell } from '../shared/BaseCell'
import { useBulkUpdateCutOffScheduleStatus } from '@/services/outboundSchedule/useOutboundScheduleQueries'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { AxiosError } from 'axios'
import { AllocationStrategy, CutType, GetOutboundSchedulesRes } from '@/services/outboundSchedule/types'
import { useCutOffHoverContext } from '../../context/CutOffHoverContext'
import { useIsIn15MinCutOff } from '../../hooks/useIsIn15MinCutOff'
import MToast from '@/utils/modal/MToast'
import useModal from '@/hooks/useModal'
import { AllocationPriorityModal } from './AllocationPriorityModal'
import { createPortal } from 'react-dom'
import { useOptionsPosition } from '../../hooks/useOptionsPosition'
import { useIsExpiredCutOff } from '../../hooks/useIsExpiredCutOff'
import { useCutOffDateTimeContext } from '../../context/CutOffRowDateTimeContext'
import { useCutOffDate } from '../../hooks/useCutOffDate'

type Props = {
  type: CutType
  scrollContainerRef: RefObject<HTMLDivElement>
  hasSchedules: boolean
  schedule?: GetOutboundSchedulesRes['timeSlots'][number]['schedules'][number]
}

const AllocationStrategyMap: Record<AllocationStrategy, string> = {
  PACKING: '합포',
  WAREHOUSE_PRIORITY: '센터',
}

type Position = { top: number; left: number } | undefined

export function CutOffCutTypeCell({ type, scrollContainerRef, hasSchedules, schedule }: Props) {
  const [optionsPosition, setOptionsPosition] = useState<Position>()
  const [isOpen, setIsOpen] = useState(false)

  const { date, time } = useCutOffDateTimeContext()
  const { isCutOffHover } = useCutOffHoverContext()

  const isExpired = useIsExpiredCutOff({ date, time })
  const isIn15MinCutOff = useIsIn15MinCutOff({ date, time })

  const priorityModal = useModal()

  const wrapRef = useRef<HTMLDivElement>(null)
  useClickOutside(wrapRef, () => setIsOpen(false), !priorityModal.open) // 내부 모달이 열려있을떄는 enabled처리

  const textColor = isExpired ? colorSystem.text.common.tertiary : colorSystem.text.common.primary
  const backgroundColor = isCutOffHover ? '#f4faff' : isExpired ? '#00000008' : 'transparent'

  useOptionsPosition({ isIn15MinCutOff, targetRef: wrapRef, setPosition: setOptionsPosition, scrollContainerRef })

  return (
    <TypeBoxWrap
      $isOpen={isOpen}
      $hasSchedules={hasSchedules}
      onClick={() => {
        if (!hasSchedules) return
        setIsOpen(true)
      }}
      ref={wrapRef}
      $backgroundColor={backgroundColor}
    >
      <Text kind="Body_12_regular" color={textColor}>
        {type}
      </Text>

      {hasSchedules && <Icon className="more-icon" name="menu" size={16} color="gray600" />}
      {hasSchedules && !isOpen && schedule?.allocationStrategy && (
        <Text className="allocation-strategy-text" kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
          {AllocationStrategyMap[schedule.allocationStrategy]}
        </Text>
      )}
      {isOpen && schedule && <CutOffOptions type={type} position={optionsPosition} priorityModal={priorityModal} schedule={schedule} />}
    </TypeBoxWrap>
  )
}

const TypeBoxWrap = styled(BaseCell)<{ $isOpen: boolean; $backgroundColor: string; $hasSchedules: boolean }>`
  flex: 1;
  position: relative;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  cursor: ${({ $hasSchedules }) => ($hasSchedules ? 'pointer' : 'default')};

  justify-content: space-between;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          .more-icon {
            display: block;
          }
        `
      : css`
          .more-icon {
            display: none;
          }
        `}

  &:hover {
    .more-icon {
      display: block;
    }

    .allocation-strategy-text {
      display: none;
    }
  }
`

/** 15분 로직이 들어가야해서 클릭시 마다 새로 렌더링 되어야함 -> 분리 */
function CutOffOptions({
  type,
  position,
  priorityModal,
  schedule,
}: {
  type: CutType
  position: Position
  priorityModal: ReturnType<typeof useModal>
  schedule: GetOutboundSchedulesRes['timeSlots'][number]['schedules'][number]
}) {
  const { date, time } = useCutOffDateTimeContext()

  const { shipperCode } = useCutOffDate()
  const isExpired = useIsExpiredCutOff({ date, time })
  const isIn15MinCutOff = useIsIn15MinCutOff({ date, time })

  const bulkUpdateCutOffScheduleStatus = useBulkUpdateCutOffScheduleStatus()

  const handleBulkUpdateSubmit = async (isActive: boolean) => {
    try {
      await bulkUpdateCutOffScheduleStatus.mutateAsync({
        date,
        time,
        isActive,
        cutType: type,
        shipperCode,
      })
      MToast.show(`컷오프가 일괄 ${isActive ? 'ON' : 'OFF'} 되었습니다.`)
    } catch (e) {
      MAlert.show(getShipperScopeErrorMessage(e) ?? Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '컷오프 일괄 ON/OFF 변경에 실패했습니다.'))
    }
  }

  // 포탈로 렌더링
  const optionsContent = !isIn15MinCutOff ? (
    <>
      <Options $width={140} style={{ ...position }}>
        {!isExpired && (
          <>
            <OptionItem onClick={() => handleBulkUpdateSubmit(true)}>컷오프 일괄 ON</OptionItem>
            <OptionItem onClick={() => handleBulkUpdateSubmit(false)}>컷오프 일괄 OFF</OptionItem>
          </>
        )}
        <OptionItem
          onClick={(e) => {
            e.stopPropagation()
            priorityModal.onOpenModal()
          }}
        >
          우선순위 설정
        </OptionItem>
      </Options>
      {priorityModal.open && (
        <AllocationPriorityModal date={date} time={time} cutType={type} schedule={schedule} onClose={priorityModal.onCloseModal} />
      )}
    </>
  ) : (
    <Options $width={180} style={{ ...position }}>
      <VStack gap={6} align="center" justify="center" style={{ height: 120, textAlign: 'center' }}>
        <Text kind="Body_12_medium">컷오프 변경 불가</Text>
        <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
          컷오프 15분 전부터는
          <br />
          모든 변경이 제한됩니다.
        </Text>
      </VStack>
    </Options>
  )

  return position ? createPortal(optionsContent, document.body) : null
}

const Options = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${colors.white};
  border: 1px solid ${colorSystem.border.element};
  z-index: 99;

  position: absolute;

  display: flex;
  flex-direction: column;
  gap: 8px;
`

const OptionItem = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    background-color: ${colors.gray050};
  }
`
